import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Music, Clock, CheckCircle, Users, Mic2 } from "lucide-react";

import { FaWhatsapp } from "react-icons/fa";
import Layout from "@/components/layout/Layout";
import SectionHeading from "@/components/shared/SectionHeading";
import TestimonialCard from "@/components/shared/TestimonialCard";
import VideoCarousel from "@/components/shared/VideoCarousel";

const Weddings = () => {
  const videos = [
    { src: "https://www.youtube.com/embed/RsLzeQFHsok", title: "Olivia Flemming Showreel" },
    { src: "https://www.youtube.com/embed/LAdKfpsZaUc", title: "Wedding Ceremony Performance" },
  ];

  const ceremonyMoments = [
    {
      icon: <Heart className="w-8 h-8" />,
      title: "Aisle Entrance",
      description: "The moment you've dreamed of. I'll perform as you walk towards your partner, setting the tone for your ceremony with a song that reflects your love story."
    },
    {
      icon: <Music className="w-8 h-8" />,
      title: "Signing of the Register",
      description: "While you sign, I'll provide a beautiful musical interlude that allows guests to reflect on the significance of the moment and enjoy a live performance."
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Recessional (Exit)",
      description: "Your first walk as a married couple deserves a joyful, celebratory song. I'll send you off into your new life together with music that fills the room."
    }
  ];

  const experiencePoints = [
    {
      icon: <Users className="w-5 h-5" />,
      text: "Pre-wedding consultation and song guidance"
    },
    {
      icon: <Mic2 className="w-5 h-5" />,
      text: "Live vocals for all ceremony moments"
    },
    {
      icon: <CheckCircle className="w-5 h-5" />,
      text: "Liaison with your registrar or celebrant"
    },
    {
      icon: <Clock className="w-5 h-5" />,
      text: "Early arrival and sound check at your venue"
    },
    {
      icon: <Heart className="w-5 h-5" />,
      text: "Calm, discreet professional presence"
    }
  ];

  const songSuggestions = [
    "Ave Maria",
    "Pie Jesu",
    "Can't Help Falling in Love",
    "The Prayer",
    "All I Ask of You",
    "Martha's Harbour"
  ];

  const testimonials = [
    {
      quote: "From our first consultation to the ceremony itself, Olivia was calm, professional, and reassuring. She took all the stress out of the music and let us focus on each other.",
      author: "Rachel & Tom",
      role: "The Belfry"
    },
    {
      quote: "Olivia exceeded all our expectations. Her voice brought tears to our guests' eyes during the ceremony. She understood exactly what we wanted and delivered it perfectly.",
      author: "Emma & David",
      role: "Pendrell Hall"
    },
    {
      quote: "Our guests are still talking about Olivia's performance. The moment I walked down the aisle will stay with me forever. Her voice was absolutely stunning.",
      author: "Charlotte & Michael",
      role: "Hoar Cross Hall"
    }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative h-screen min-h-[700px] overflow-hidden bg-secondary">
        <div className="absolute inset-0">
          <img
            src="/vocalist-Wedding-west-midlands.png"
            alt="Olivia singing at a wedding ceremony"
            className="w-full h-full object-cover object-[center_10%] sm:object-[center_15%] md:object-[center_20%]"
            loading="eager"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/60" />
        </div>
        
        <div className="relative h-full flex flex-col items-center justify-center text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <p className="text-cream/80 text-sm uppercase tracking-widest mb-4">
              Specialist Ceremony Vocalist
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-playfair font-semibold text-white mb-4">
              Wedding Ceremony Music
            </h1>
            <p className="text-xl md:text-2xl text-cream/90 font-light mb-8 max-w-xl mx-auto">
              Calm, timeless vocals for the most important moment of your day
            </p>
            
            <button
              onClick={() => document.getElementById('video-section')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center gap-3 bg-white/95 text-secondary px-10 py-5 rounded-full font-semibold text-lg shadow-lg hover:bg-white transition-all"
            >
              🎵 Hear My Voice
            </button>

            <div className="mt-4">
              <a
                href="https://wa.me/447702696253?text=Hi%20Olivia%2C%20I'm%20interested%20in%20ceremony%20music%20for%20my%20wedding.%0A%0AWedding%20date%3A%0AVenue%3A%0ACeremony%20time%3A"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-green-600 text-white px-7 py-3.5 rounded-full font-medium text-base hover:bg-green-700 transition-all"
              >
                <FaWhatsapp className="w-5 h-5" />
                Message Me on WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <h2 className="text-3xl md:text-4xl font-playfair font-semibold text-secondary">
              Your Ceremony Deserves a Specialist
            </h2>
            <p className="text-lg text-secondary/80 leading-relaxed">
              Your wedding ceremony is the heart of your day and the moment you and your guests will remember forever. I focus exclusively on ceremony music because I believe this moment deserves dedicated attention.
            </p>
            <p className="text-lg text-secondary/80 leading-relaxed">
              As your ceremony vocalist, I bring a calm, experienced presence. I will guide you through song selection, liaise with your registrar or celebrant, and ensure every musical moment runs seamlessly.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Ceremony Moments */}
      <section className="py-16 md:py-24 bg-cream">
        <div className="max-w-6xl mx-auto px-4">
          <SectionHeading 
            title="Ceremony Moments"
            subtitle="Three key moments where live vocals transform your ceremony"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {ceremonyMoments.map((moment, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center p-8 bg-white rounded-lg shadow-sm"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full text-primary mb-4">
                  {moment.icon}
                </div>
                <h3 className="text-xl font-playfair font-semibold text-secondary mb-3">
                  {moment.title}
                </h3>
                <p className="text-secondary/70 leading-relaxed">
                  {moment.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Ceremony Vocal Experience */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <SectionHeading 
            title="The Ceremony Vocal Experience"
            subtitle="What's included when you book me for your wedding ceremony"
          />
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-4"
          >
            {experiencePoints.map((point, index) => (
              <div 
                key={index} 
                className="flex items-center gap-4 p-5 bg-cream/50 rounded-lg"
              >
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center text-primary flex-shrink-0">
                  {point.icon}
                </div>
                <span className="text-lg text-secondary/80">{point.text}</span>
              </div>
            ))}
          </motion.div>

          {/* Pricing */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-12 text-center p-8 bg-secondary/5 rounded-lg border border-secondary/10"
          >
            <p className="text-secondary/60 text-sm uppercase tracking-wide mb-2">Investment</p>
            <p className="text-3xl font-playfair font-semibold text-secondary mb-2">Starting from £450</p>
            <p className="text-secondary/60 text-sm">
              Includes pre wedding consultation, full ceremony performance, and travel within 25 miles of Sutton Coldfield. Travel beyond this area is available on request.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Song Suggestions */}
      <section className="py-16 md:py-24 bg-cream">
        <div className="max-w-4xl mx-auto px-4">
          <SectionHeading 
            title="Popular Ceremony Songs"
            subtitle="A selection of frequently requested songs. I'll also help you choose the perfect music during our consultation."
          />
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-3"
          >
            {songSuggestions.map((song, index) => (
              <div 
                key={index} 
                className="flex items-center justify-center p-4 bg-white rounded-lg"
              >
                <span className="font-medium text-secondary">{song}</span>
              </div>
            ))}
          </motion.div>
          
          <p className="text-center text-secondary/60 mt-6 text-sm">
            Don't see your song? I can learn special requests that are meaningful to you both.
          </p>
        </div>
      </section>

      {/* Video Section */}
      <section id="video-section" className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <SectionHeading 
            title="Watch and Listen"
            subtitle="Ceremony performances"
          />
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <VideoCarousel videos={videos} />
          </motion.div>

          <div className="mt-8 text-center">
            <a
              href="https://wa.me/447702696253?text=Hi%20Olivia%2C%20I'm%20interested%20in%20ceremony%20music%20for%20my%20wedding.%0A%0AWedding%20date%3A%0AVenue%3A%0ACeremony%20time%3A"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-green-600 text-white px-8 py-4 rounded-full font-medium text-lg hover:bg-green-700 transition-all"
            >
              <FaWhatsapp className="w-5 h-5" />
              Message Me
            </a>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 md:py-24 bg-cream">
        <div className="max-w-6xl mx-auto px-4">
          <SectionHeading 
            title="What Couples Say"
            subtitle="Words from couples who trusted me with their ceremony"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <TestimonialCard
                key={index}
                quote={testimonial.quote}
                author={testimonial.author}
                role={testimonial.role}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-secondary">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-playfair font-semibold text-cream mb-4">
              Let's Discuss Your Ceremony
            </h2>
            <p className="text-lg text-cream/80 mb-8 max-w-xl mx-auto">
              I'd love to hear about your wedding and help you choose the perfect ceremony music. Send me a message with your date and venue, and I'll be in touch within 24 hours.
            </p>
            
            <a
              href="https://wa.me/447702696253?text=Hi%20Olivia%2C%20I'm%20interested%20in%20ceremony%20music%20for%20my%20wedding.%0A%0AWedding%20date%3A%0AVenue%3A%0ACeremony%20time%3A"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-green-600 text-white px-8 py-4 rounded-full font-medium text-lg hover:bg-green-700 transition-all"
            >
              <FaWhatsapp className="w-5 h-5" />
              Message Me on WhatsApp
            </a>
            
            <p className="text-cream/50 text-sm mt-6">
              Available for ceremonies across Birmingham, Solihull, Sutton Coldfield, Lichfield, and the West Midlands
            </p>
            <p className="text-cream/50 text-sm mt-3">
              Planning your music? Read <Link to="/journal/wedding-singer-birmingham-solihull" className="underline underline-offset-4 hover:text-primary">choosing a wedding singer in Birmingham and Solihull</Link>.
            </p>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default Weddings;
